import React, { useMemo, useState } from 'react';
import StorageService from 'utilities/storageService';
import {
  LOCAL_STORAGE_SHOP_SELECTED_PROVIDER,
  pageIds,
} from 'utilities/constants';
import { useGetProvidersByPatientIdQuery } from 'graphql/generated/remote-schema-hasura';
import { Option } from 'react-multi-select-component';
import Select from 'react-select';
import ButtonComponent from 'components/button/buttonComponent';
import { useGetPage } from 'hooks/useGetPage';
import {
  PROVIDERS,
  SHOP,
  SHOP_CHECKOUT_LINK,
  TEST_KITS_MANAGEMENT,
} from 'utilities/routes';
import { useNavigate } from 'react-router-dom';
import ModalComponent from 'components/modal/modalComponent';
import { twMerge } from 'tailwind-merge';
import { CartProductOutput } from 'graphql/generated/hasura';
import { useAuth } from 'auth/context/AuthContext';
import { ProductCategory } from '../../../superadmin/pages/products/list/useGetTableConfig';
import ProductByUser, { PatientOutput, Product } from './ProductByUser';

const selectedProviderStorage = new StorageService(
  LOCAL_STORAGE_SHOP_SELECTED_PROVIDER,
  localStorage,
  '',
);

interface CartModalProps {
  onClose: () => void;
  isOpen: boolean;
  totalPrice: number;
  updateSelectedProducts: React.Dispatch<
    React.SetStateAction<Record<string, number>>
  >;
  updateIsLoadingCartMutation: React.Dispatch<React.SetStateAction<boolean>>;
  cartProducts: CartProductOutput[];
  isLoadingRequests: boolean;
  isLoadingCartData: boolean;
  isLoadingCartMutation: boolean;
}

export function groupProductsByPatientMap(
  cartProducts: CartProductOutput[],
): Map<
  string,
  { patientOwner: PatientOutput; provider: Option; products: Product[] }
> {
  return cartProducts.reduce((map, cartItem) => {
    const patient = cartItem.patientOwner;
    const provider = cartItem.provider;
    const patientId = patient ? patient.id : 'unassigned';

    if (!map.has(patientId)) {
      map.set(patientId, {
        patientOwner: patient,
        provider: {
          value: provider?.id,
          label: `${provider?.SENSITIVE_firstname} ${provider?.SENSITIVE_lastname}`,
        },
        products: [],
      });
    }
    map.get(patientId).products.push({
      cartProductId: cartItem.id,
      quantity: cartItem.quantity,
      ...cartItem.product,
    });

    return map;
  }, new Map());
}

const CartModal: React.FC<CartModalProps> = ({
  onClose,
  isOpen,
  totalPrice,
  isLoadingCartData,
  updateSelectedProducts,
  isLoadingRequests,
  cartProducts,
  updateIsLoadingCartMutation,
  isLoadingCartMutation,
}) => {
  const [myProviders, updateMyProviders] = useState<Array<Option>>([]);
  const [selectedProvider, updateSelectedProvider] = useState<
    Option | undefined
  >();
  const navigate = useNavigate();
  const { selectedAccount, user: loggedUser } = useAuth();
  const { data: locale, loading } = useGetPage({
    locale: 'en',
    pageId: pageIds.SHOP_DIAGNOSTIC,
  });

  const goToSelectProvider = () => {
    navigate(PROVIDERS, {
      state: {
        isFromShoppingCart: true,
      },
    });
  };

  const hasProvider = !!myProviders.length;
  const hasTests = !!cartProducts.filter(
    ({ product }) => product?.category === ProductCategory.Test_Kits,
  ).length;

  useGetProvidersByPatientIdQuery({
    fetchPolicy: 'network-only',
    variables: {
      patientCodexId: selectedAccount || loggedUser?.uuid,
    },
    onError: (error) => {
      console.error('Error getting providers', error);
      return;
    },
    onCompleted: (data) => {
      const providersList = data.getProvidersByPatientId?.providers.map(
        ({ id, SENSITIVE_firstname, SENSITIVE_lastname }) => ({
          label: `${SENSITIVE_firstname} ${SENSITIVE_lastname}`,
          value: id,
        }),
      );
      updateMyProviders(providersList);
      const savedSelectedProvider = selectedProviderStorage.getData();
      if (!savedSelectedProvider && hasTests) {
        updateSelectedProvider(providersList[0]);
        selectedProviderStorage.setData(providersList[0]);
        return;
      }
      updateSelectedProvider(savedSelectedProvider);
    },
  });

  const cards = useMemo(
    () => Object.fromEntries(groupProductsByPatientMap(cartProducts)),
    [cartProducts],
  );

  if (!locale || loading) return null;

  return (
    <ModalComponent
      modalDesktopWidth="desktop:max-w-[1100px]"
      contentContainerDefaultBackground="bg-base-content h-full desktop:h-fit desktop:max-h-[70vh] overflow-y-auto scrollbar-thin scrollbar-thumb-rounded-full scrollbar-track-rounded-full"
      isOpen={isOpen}
      title="Cart"
      onClose={onClose}
      customBottom={
        <>
          <div
            className={twMerge(
              'flex w-full flex-row items-center gap-5 desktop:gap-30',
            )}
          >
            <ButtonComponent
              testID="close"
              type="underline"
              className="desktop:block hidden mr-auto"
              onClick={() => {
                onClose();
                navigate(`${TEST_KITS_MANAGEMENT}${SHOP}`);
              }}
            >
              {'Continue shopping'}
            </ButtonComponent>
            <div className="font-semibold text-h4 text-neutral-800">
              Price: ${totalPrice}
            </div>
            <ButtonComponent
              testID="purchase"
              showSpinner={isLoadingCartMutation || isLoadingCartData}
              disabled={
                isLoadingCartMutation ||
                isLoadingCartData ||
                !cartProducts.length
              }
              className={twMerge(
                '!w-fit ml-auto desktop:ml-0 !text-sm whitespace-nowrap !px-9',
                !myProviders.length && 'hidden',
              )}
              onClick={() => {
                navigate(SHOP_CHECKOUT_LINK);
                onClose();
              }}
            >
              Checkout
            </ButtonComponent>
          </div>
        </>
      }
    >
      {hasProvider && hasTests && (
        <div className="bg-white hidden rounded-10 p-5 w-full flex-col desktop:flex-row desktop:gap-30 gap-5 mt-4">
          <div className="text-med-gray-3">
            Please select a health care provider who will receive your results
            and counsel you on the meaning of your results. You will need to
            book an appointment with this provider once your tests results are
            ready.
          </div>
          <div className="flex flex-col desktop:flex-row gap-2.5 items-center">
            <div className="flex items-start desktop:items-center gap-0.5 desktop:gap-4 w-full desktop:w-fit flex-col desktop:flex-row">
              <label className="font-semibold text-btn-black hidden desktop:block">
                {locale?.provider}
              </label>
              <Select
                className="dropdown desktop:max-w-[200px] desktop:w-[200px] w-full"
                classNamePrefix="react-select"
                value={selectedProvider}
                options={myProviders}
                onChange={(option) => {
                  updateSelectedProvider(option as Option);
                  selectedProviderStorage.setData(option);
                }}
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              />
            </div>

            <ButtonComponent
              className="h-fit !text-sm"
              testID="select-new-provider-button"
              type="outlined"
              onClick={goToSelectProvider}
            >
              <div className="whitespace-nowrap">Select New Provider</div>
            </ButtonComponent>
          </div>
        </div>
      )}

      <div
        className={twMerge(
          'hidden flex-col gap-2.5 desktop:h-fit py-20 pb-12 px-11 text-center',
          !cartProducts.length && '!flex',
        )}
      >
        <p className="text-neutral-800 text-h5 font-semibold">
          No products yet
        </p>
        <p className="text-med-gray-3 font-medium">
          Please go to the shop to add products
        </p>
      </div>

      <div
        className={twMerge(
          'flex flex-col gap-2.5',
          !cartProducts.length && 'hidden',
        )}
      >
        {Object.entries(cards).map(
          ([id, { products, patientOwner, provider }]) => (
            <ProductByUser
              products={products}
              updateSelectedProducts={updateSelectedProducts}
              updateIsLoadingCartMutation={updateIsLoadingCartMutation}
              isLoadingRequests={isLoadingRequests}
              patientOwner={patientOwner}
              patientCodexId={id}
              provider={provider}
            />
          ),
        )}
      </div>
    </ModalComponent>
  );
};

export default CartModal;
